import { phraseMatchPattern, phraseWordCount } from './phraseUtils';
import type { ChatLinkTarget } from './types';

/** Whole-word token in the message (letters/digits only — never split on punctuation). */
const MESSAGE_WHOLE_WORD = /^[A-Za-z0-9]+/;

/** True when phrase matches exactly at [start, end) in the original message (no message normalization). */
export function phraseMatchesAt(text: string, start: number, end: number, phrase: string): boolean {
  const regex = phraseMatchPattern(phrase);
  regex.lastIndex = start;
  const match = regex.exec(text);
  return match !== null && match.index === start && match.index + match[0].length === end;
}

/**
 * Among targets, keep those whose longest phrase matching this span ties for the most words
 * (then longest characters). Shorter shared phrases lose to a more specific fund title.
 */
export function disambiguateTargetsAtSpan(
  text: string,
  start: number,
  end: number,
  targets: ChatLinkTarget[],
): ChatLinkTarget[] {
  let maxWords = 0;
  let maxLen = 0;
  const result: ChatLinkTarget[] = [];

  for (const target of targets) {
    let bestWords = 0;
    let bestLen = 0;
    for (const phrase of target.matchPhrases) {
      if (!phraseMatchesAt(text, start, end, phrase)) continue;
      const words = phraseWordCount(phrase);
      if (words > bestWords || (words === bestWords && phrase.length > bestLen)) {
        bestWords = words;
        bestLen = phrase.length;
      }
    }
    if (bestWords === 0) continue;

    if (bestWords > maxWords || (bestWords === maxWords && bestLen > maxLen)) {
      maxWords = bestWords;
      maxLen = bestLen;
      result.length = 0;
      result.push(target);
    } else if (bestWords === maxWords && bestLen === maxLen) {
      if (!result.some((entry) => entry.id === target.id)) {
        result.push(target);
      }
    }
  }

  return result;
}

function maxMatchingPhraseWordsAtSpan(
  text: string,
  start: number,
  end: number,
  targets: ChatLinkTarget[],
): number {
  let max = 0;
  for (const target of targets) {
    for (const phrase of target.matchPhrases) {
      if (!phraseMatchesAt(text, start, end, phrase)) continue;
      max = Math.max(max, phraseWordCount(phrase));
    }
  }
  return max;
}

function tryReadNextWholeWord(
  text: string,
  position: number,
): { word: string; end: number } | null {
  let pos = position;
  while (pos < text.length && /\s/.test(text[pos])) {
    pos += 1;
  }
  if (pos >= text.length) return null;
  const match = text.slice(pos).match(MESSAGE_WHOLE_WORD);
  if (!match) return null;
  return { word: match[0], end: pos + match[0].length };
}

/**
 * After a phrase match, absorb following whole words from the message when they
 * narrow or lengthen the match (e.g. "Wantirna" + "A" → "Wantirna A" for one fund).
 */
export function extendMatchSpanWithFollowingWords(
  text: string,
  start: number,
  end: number,
  targets: ChatLinkTarget[],
): { end: number; targets: ChatLinkTarget[] } {
  let bestEnd = end;
  let bestTargets = disambiguateTargetsAtSpan(text, start, end, targets);

  while (true) {
    const next = tryReadNextWholeWord(text, bestEnd);
    if (!next) break;

    const extendedTargets = disambiguateTargetsAtSpan(text, start, next.end, targets);
    if (extendedTargets.length === 0) break;

    const previousWords = maxMatchingPhraseWordsAtSpan(text, start, bestEnd, targets);
    const extendedWords = maxMatchingPhraseWordsAtSpan(text, start, next.end, targets);
    const narrows = extendedTargets.length < bestTargets.length;
    if (extendedWords <= previousWords && !narrows) break;

    bestEnd = next.end;
    bestTargets = extendedTargets;
  }

  return { end: bestEnd, targets: bestTargets };
}
